import { Slider } from 'antd';
import { ColorContext } from 'App';
import FullScreenMap from 'components/FullScreenMap';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import Cookies from 'js-cookie';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Popup from 'reactjs-popup';
import styles from './assets/Doppler.module.css';
import Controls from './components/Controls';
import { generateFrameTimes, roundToNearestTenMinutes } from './dopplerApi';
import DopplerTileLayers, { PrefetchProgressIndicator } from './DopplerTileLayers';

const Doppler = ({ widget, dimensions, preview, sidebarOpen }) => {
  const [sliderValue, setSliderValue] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [opacity, setOpacity] = useState(0.7);
  const [prefetchProgress, setPrefetchProgress] = useState(null);

  const [satelliteView, setSatelliteView] = useState(false);
  const [labelsEnabled, setLabelsEnabled] = useState(true);

  const mapRef = useRef();
  const layerGroups = useRef(new Array(12));

  const southWest = [24.396308, -125.0];
  const northEast = [49.384358, -66.93457];
  const bounds = [southWest, northEast];

  const [fetching, setFetching] = useState(false);

  const [userLocation, setUserLocation] = useState(null);

  const theme = useContext(ColorContext);

  const [mapCenter, setMapCenter] = useState({
    lat: Cookies.get('latitude'),
    lng: Cookies.get('longitude'),
  });

  const handleSliderChange = (value) => {
    setSliderValue(value);
  };

  // Callback for prefetch progress updates
  const handlePrefetchProgress = useCallback((progress) => {
    setPrefetchProgress(progress);
  }, []);

  // Memoize frames to prevent recalculating on every render
  const frames = useMemo(() => generateFrameTimes(12, 10), []);
  const baseTime = useMemo(() => roundToNearestTenMinutes(new Date()), []);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation([position.coords.latitude, position.coords.longitude]);
      },
      (error) => {
        console.error('Error getting user location', error);
      },
    );
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPlaying) {
        setSliderValue((prevValue) => (prevValue + 1) % frames.length);
      }
    }, 1000 / playbackSpeed);
    return () => clearInterval(interval);
  }, [isPlaying, sliderValue, playbackSpeed]);

  if (!widget)
    return (
      <div className={`h-100 w-100 d-flex flex-column user-select-none`}>
        <WidgetTimeUpdate //Time
          route={'Doppler'}
          updateIntervalBasis={10}
          addMargin
        />
        <div className={`d-flex flex-column h-100 w-100 ${styles['map-container']}`}>
          <div>
            <h1 className={`fs-1 z-2 text-white ${styles['timeframe-text']}`}>
              {' '}
              <strong>
                {frames[sliderValue].toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </strong>
            </h1>
          </div>
          <div
            className={`d-none d-lg-flex border-2 bg-primary-shadow p-3  rounded-2 z-2 ${styles['controls-container']}`}
          >
            <Controls //Large screen Control Bar
              isPlaying={isPlaying}
              setIsPlaying={setIsPlaying}
              playbackSpeed={playbackSpeed}
              setPlaybackSpeed={setPlaybackSpeed}
            />
          </div>

          <Slider //time slider | Use Style class for pos
            className={`d-none  d-lg-flex flex-shrink-1 z-2 rounded mb-3 bg-white border border-1 border-black ${styles['playbar']}`}
            min={0}
            max={11}
            value={sliderValue}
            onChange={handleSliderChange}
            tooltip={{
              formatter: (value) =>
                frames[value].toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
            }}
          />

          <Popup //For smaller screen size on fullscreen
            trigger={
              <div
                className={`d- d-lg-none w-fit bg-primary-shadow rounded-1 border-2 p-2 z-2 ${styles['controls-popup']}`}
              >
                Animation Controls
              </div>
            }
            on={['hover', 'click']}
            position="top left"
            onhover
            children={
              <div
                className={`d-flex d-lg-none bg-primary-shadow d-inline-flex z-3 text-white border-4 rounded-4 ${styles['popup-controls-container']}`}
              >
                <div className="pt-3 ps-2 pe-1">
                  <Controls
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying}
                    playbackSpeed={playbackSpeed}
                    setPlaybackSpeed={setPlaybackSpeed}
                  />
                  <Slider
                    min={0}
                    max={11}
                    value={sliderValue}
                    onChange={handleSliderChange}
                    tooltip={{
                      formatter: (value) =>
                        frames[value].toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        }),
                    }}
                  />
                </div>
              </div>
            }
          ></Popup>
          <PrefetchProgressIndicator progress={prefetchProgress} />
          <FullScreenMap //Map
            mapRef={mapRef}
            mapCenter={mapCenter}
            zoom={5}
            minZoom={5}
            maxZoom={18}
            showMapSat={false}
            isDopplerMap={true}
            classNameStyle={`d-inline-flex z-2 justify-content-end align-items-start flex-nowrap m-0 p-0 ${styles['sattellite-switch']}`}
          >
            <DopplerTileLayers
              baseTime={baseTime}
              frames={frames}
              currentFrame={sliderValue}
              isPlaying={isPlaying}
              onPrefetchProgress={handlePrefetchProgress}
            />
          </FullScreenMap>
        </div>
        <div className="bg-base">
          <div
            className="h-100 d-flex gap-3 mt-0 mt-lg-3 z-3 px-2 py-3 justify-content-center" //Precipitation bar
          >
            Precipitation{' '}
            <div className={`border-2 rounded-3 ${styles['precipitation-bar']}`}></div>
          </div>
        </div>
      </div>
    );

  if (fetching) return <WidgetLoadingAnimation widgetName={'Doppler'} dimensions={dimensions} />;
  //SM breakpoint
  if (widget && userLocation)
    return (
      <div className={`h-100 w-100 d-flex flex-column user-select-none overflow-hidden p-2`}>
        <WidgetTimeUpdate //Time
          route={'Doppler'}
          updateIntervalBasis={10}
          addMargin
        />
        <div className={`d-flex flex-column h-100 w-100 ${styles['map-container']}`}>
          <div>
            <h1 className={`fs-4 z-3 text-white ${styles['widget-timeframe-text']}`}>
              {' '}
              <strong>
                {frames[sliderValue].toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </strong>
            </h1>
          </div>
          <Popup
            trigger={
              <div
                className={`d-flex w-fit bg-primary-shadow rounded-1 border-2 p-2 z-3 ${styles['controls-popup']}`}
              >
                Animation Controls
              </div>
            }
            on={['hover', 'click']}
            position="top left"
            onhover
            children={
              <div
                className={`bg-primary-shadow d-inline-flex z-3 text-white border-4 rounded-4 ${styles['popup-controls-container']}`}
              >
                <div className="pt-3 ps-2 pe-1">
                  <Controls
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying}
                    playbackSpeed={playbackSpeed}
                    setPlaybackSpeed={setPlaybackSpeed}
                  />
                  <Slider
                    min={0}
                    max={11}
                    value={sliderValue}
                    onChange={handleSliderChange}
                    tooltip={{
                      formatter: (value) =>
                        frames[value].toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        }),
                    }}
                  />
                </div>
              </div>
            }
          ></Popup>
          <div
            className={`d-flex justify-content-start align-items-center py-3 rounded-4 gap-2 z-3 position-absolute ${styles['widget-precipitation-bar-container']}`}
          >
            Precipitation <div className={`${styles['widget-precipitation-bar']}`}></div>
          </div>
          <PrefetchProgressIndicator progress={prefetchProgress} />
          <FullScreenMap //Map at z-1
            mapRef={mapRef}
            mapCenter={mapCenter}
            zoom={5}
            minZoom={5}
            maxZoom={18}
            showMapSat={false}
            classNameStyle={`d-inline-flex z-2 justify-content-end align-items-start flex-nowrap m-0 p-0 ${styles['widget-sattellite-switch']}`}
            isDopplerMap={true}
          >
            <DopplerTileLayers
              baseTime={baseTime}
              frames={frames}
              currentFrame={sliderValue}
              isPlaying={isPlaying}
              onPrefetchProgress={handlePrefetchProgress}
            />
          </FullScreenMap>
        </div>
      </div>
    );
};

export default Doppler;
